import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { timeout } from "rxjs/operators";
import * as moment from "moment";
import { AppService } from "src/_services/app.service";
import {
  mtsDarkMode,
  setMtsDarkMode,
  clampMtsRange,
  MTS_MAX_RANGE_DAYS,
} from "../mts-reporting-summary/mts-theme";

// See the note on the summary page: the shared HTTP interceptor returns the raw
// error from its catchError instead of an observable, so on a failed request
// neither callback is ever called. Without this the page sits on "Loading…".
const REQUEST_TIMEOUT_MS = 30000;

// The MARKET calendar, which is what a `day` means to this endpoint - see
// slotreport.ReportingTZOffsetMinutes. The USER tier is UTC and is not read
// here; the two are deliberately different.
const REPORTING_TZ_OFFSET_MINUTES = 330;

type PanelKey = "top" | "bottom";

interface Row {
  key: string;
  label: string;
  acceptedBets: number;
  acceptedTurnover: number;
  rejectedBets: number;
  rejectedTurnover: number;
  cancelledBets: number;
  cancelledTurnover: number;
  rejectedPct: number;
  stake: number;
  averageStake: number;
  unsettledBets: number;
  unsettledTurnover: number;

  settledBets: number;
  settledTurnover: number;
  won: number;
  lost: number;
  winRatePct: number;
  cashOuts: number;
  cashOutAmount: number;

  pnl: number;
  // marginPct is P&L over ACCEPTED turnover - the figure an MTS console shows,
  // and the only margin this page displays. marginOnSettled divides by SETTLED
  // turnover and is still served; it is carried here so the shape matches what
  // the API sends, not because anything renders it.
  marginPct: number;
  marginOnSettled: number;

  // VOIDED, not cancelled. Served and carried, deliberately not a column -
  // see the note under COLUMNS.
  voided: number;
  voidedTurnover: number;
}

interface Column {
  key: keyof Row;
  label: string;
  kind: "money" | "int" | "pct";
  signed?: boolean;
  hint?: string;
}

// THE ACCEPTANCE SIDE, plus what was refused or pulled back. One list, and a
// short one on purpose.
//
// The settlement measures - settled bets and turnover, won, lost, win rate,
// cash-outs and the margin over settled turnover - are all SERVED, and the
// backend goes on storing them. They are simply not shown here. This page
// answers what the book TOOK in a period and what it has not yet resolved;
// adding the outcome columns beside that doubles the width to answer a
// different question nobody opened this page for.
//
// There used to be an Accepted/Settled toggle with a column list each. When
// the settled slot was removed the two lists were merged into one, which was
// wrong in the other direction - it put every settlement column on a page that
// had never shown them. This is the accepted list it always had, with the
// rejection and cancellation columns added.
const COLUMNS: Column[] = [
  { key: "acceptedBets", label: "Accepted Bets", kind: "int" },
  {
    key: "acceptedTurnover",
    label: "Accepted Turnover",
    kind: "money",
    hint: "Money at risk: the stake for a BACK, the liability for a LAY.",
  },
  { key: "stake", label: "Stake", kind: "money" },
  { key: "averageStake", label: "Avg Stake", kind: "money" },
  {
    key: "rejectedBets",
    label: "Rejected",
    kind: "int",
    hint: "Bets the book refused. They never existed, so they are counted apart from the rest.",
  },
  {
    key: "rejectedPct",
    label: "Rejected %",
    kind: "pct",
    hint: "Rejections over everything attempted - accepted plus rejected.",
  },
  { key: "rejectedTurnover", label: "Rejected Turnover", kind: "money" },
  {
    key: "cancelledBets",
    label: "Cancelled",
    kind: "int",
    hint: "Placed and then pulled back. Not the same as rejected, and not the same as voided.",
  },
  {
    key: "cancelledTurnover",
    label: "Cancelled Turnover",
    kind: "money",
    hint:
      "What those bets had put at risk before they were pulled back. It is NOT part of Accepted Turnover any more, which is why it reads separately.",
  },
  { key: "unsettledBets", label: "Unsettled Bets", kind: "int" },
  { key: "unsettledTurnover", label: "Unsettled Turnover", kind: "money" },
  {
    key: "pnl",
    label: "P&L",
    kind: "money",
    signed: true,
    hint: "The BOOK's, not the player's: positive means the house is up.",
  },
  { key: "marginPct", label: "Margin %", kind: "pct", signed: true },
];

// NO Cancelled column. What the reporting path stores is VOIDS - bets the
// settlement pipeline ended without an outcome - and a real cancel comes from
// a source this path does not read yet. Same reasoning, and the same longer
// note, as the summary page.

@Component({
  selector: "app-mts-day-detail",
  templateUrl: "./mts-day-detail.component.html",
  styleUrls: ["./mts-day-detail.component.scss"],
})
export class MtsDayDetailComponent implements OnInit {
  // The CSV uses the same list, so a download matches the table.
  get columns(): Column[] {
    return COLUMNS;
  }

  // SAPAdmin and nothing else, matching the API - see the summary page.
  canView = (JSON.parse(sessionStorage.getItem("currentUser")) || {}).role === "SAPAdmin";

  day = 0;
  dateLabel = "";
  operatorIds: string[] = [];

  // Inherited from the summary page, so the drill-down is of the same book.
  // The API requires it.
  groupProvider: "Exchange" | "SportsBook" = "SportsBook";

  // One table per section, each a FULL breakdown of the same rows rather than
  // a split of one measure - live/pre-match is a bucket dimension, so every
  // figure in these tables is scoped to it.
  //
  // All is not pre-match plus live: a bet that could not be classified - a
  // combo carries event_id "custom" and has no single start to measure
  // against - is in the first and in neither of the others.
  sections = [
    { title: "Sports", livePre: "", rows: [] as Row[], totals: null as Row, error: "",
      singlesByKey: {} as { [key: string]: Row }, singlesTotals: null as Row },
    { title: "Sports / Pre-Match", livePre: "PREMATCH", rows: [] as Row[], totals: null as Row, error: "",
      singlesByKey: {} as { [key: string]: Row }, singlesTotals: null as Row },
    { title: "Sports / Live", livePre: "LIVE", rows: [] as Row[], totals: null as Row, error: "",
      singlesByKey: {} as { [key: string]: Row }, singlesTotals: null as Row },
  ];

  // The column bands each table draws: the same columns over all bets, then
  // over singles alone, which is how the provider's own report reads.
  readonly bands = [
    { title: "Multiples & Singles", singles: false },
    { title: "Singles", singles: true },
  ];

  // The figures a band shows for a row. A sport bet on only in combos has no
  // singles row at all, and the table shows that rather than a zero.
  cellRow(row: any, section: any, band: { singles: boolean }): any {
    return band.singles ? section.singlesByKey[String(row.key || "")] || null : row;
  }

  bandTotals(section: any, band: { singles: boolean }): any {
    return band.singles ? section.singlesTotals : section.totals;
  }
  loading = false;
  errorMessage = "";

  // STANDALONE means the page was opened from the menu rather than by clicking
  // a date, so it has no day to report on yet.
  //
  // It deliberately does not load on arrival. The deep-linked page answers a
  // question the user already asked by clicking a date; this one has only a
  // default, and running a day's worth of queries to answer a question nobody
  // asked is a wasted round trip on every visit - and shows figures that look
  // chosen when they were not.
  standalone = false;
  loaded = false;

  // A RANGE, clamped to the same ceiling as the summary page. The deep-linked
  // page is still a single day - it answers "this date" because that is what
  // was clicked - so the component carries both and sends whichever it has.
  selected: any = null;
  rangeNote = "";
  ranges: any = {
    Today: [moment().startOf("day"), moment().endOf("day")],
    Yesterday: [
      moment().subtract(1, "days").startOf("day"),
      moment().subtract(1, "days").endOf("day"),
    ],
    "Last 7 Days": [moment().subtract(6, "days").startOf("day"), moment().endOf("day")],
  };

  // The best and worst EVENTS of the period, by the book's P&L.
  //
  // One request, sorted both ways - the two panels are the same rows read from
  // opposite ends, so asking twice would be two scans of the same day for an
  // answer that cannot disagree with itself.
  events = {
    top: [] as Row[],
    bottom: [] as Row[],
    topOpen: false,
    bottomOpen: false,
    loading: false,
    error: "",
    // Where the ranking's data ends, in epoch millis - see the API's AsOf.
    // Zero means nothing to qualify.
    asOf: 0,
    // The PERIOD's totals, which the server sends with a ranking. NOT the sum
    // of the rows shown - those are twenty out of thousands, and twenty more
    // that are a different twenty.
    totals: null as Row,
    // Whether the current filters have been fetched yet. The panels are
    // collapsed on arrival and most visits never open one, so the ranking is
    // a whole extra aggregation nobody reads; it waits to be asked for.
    fetched: false,
    // Bumped on every fetch. A reply whose token is stale belongs to filters
    // the user has already moved off, and applying it would put the old
    // provider's ranking under the new provider's heading.
    seq: 0,
  };
  readonly eventPanelSize = 20;

  // Declared rather than written twice in the template: the two panels differ
  // only in which end of the same ranking they show.
  readonly eventPanels: { key: PanelKey; title: string; empty: string }[] = [
    {
      key: "top",
      title: "Events / Top 20 (by profit)",
      // Not "no events": the period may have had plenty and made money on
      // none of them. Mirrors the bottom panel's wording for the same reason.
      empty: "The book made nothing on any event in this period.",
    },
    {
      key: "bottom",
      title: "Events / Bottom 20 (by loss)",
      // Not "no events": the day may have had plenty and lost on none of them.
      empty: "The book lost on no event in this period.",
    },
  ];

  isPanelOpen(key: PanelKey): boolean {
    return key === "top" ? this.events.topOpen : this.events.bottomOpen;
  }

  togglePanel(key: PanelKey) {
    if (key === "top") {
      this.events.topOpen = !this.events.topOpen;
    } else {
      this.events.bottomOpen = !this.events.bottomOpen;
    }
    // Fetched on the first open, and shared by both panels: one request
    // carries both ends, so opening the second costs nothing.
    if ((this.events.topOpen || this.events.bottomOpen) && !this.events.fetched) {
      this.loadEvents();
    }
  }

  panelRows(key: PanelKey): Row[] {
    return key === "top" ? this.events.top : this.events.bottom;
  }

  // Shared with the summary page, so clicking a date does not change the
  // theme halfway through a report.
  dark = mtsDarkMode();

  toggleTheme() {
    this.dark = !this.dark;
    setMtsDarkMode(this.dark);
  }

  constructor(
    private appService: AppService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    if (!this.canView) {
      return;
    }
    this.route.paramMap.subscribe((params) => {
      this.day = Number(params.get("day")) || 0;
      this.standalone = this.day === 0;
      this.dateLabel = this.formatDay(this.day);
      // The filters the summary was showing, so the drill-down is of the same
      // book rather than of everything. Carried in the URL, which also makes
      // the page shareable and survives a reload.
      const qp = this.route.snapshot.queryParamMap;
      const gp = qp.get("groupProvider");
      this.groupProvider = gp === "Exchange" ? "Exchange" : "SportsBook";
      const ops = qp.get("operators");
      this.operatorIds = ops ? ops.split(",").filter((x) => !!x) : [];

      if (this.standalone) {
        // Today, pre-filled but NOT run. See `standalone`.
        this.selected = { startDate: moment().startOf("day"), endDate: moment().endOf("day") };
        return;
      }
      this.load();
    });
  }

  // Runs the report the standalone page has been sitting on.
  //
  // The day is read in the REPORTING timezone, not the browser's. The server
  // anchors a YYYYMMDD with DaySlotRange, so a browser elsewhere that sent its
  // own local day would ask for a different span of hours than the summary
  // page shows under the same heading.
  apply() {
    if (!this.selected || !this.selected.startDate || !this.selected.endDate) {
      this.errorMessage = "Pick a date range first.";
      return;
    }
    this.errorMessage = "";
    this.dateLabel = this.rangeLabel();
    this.load();
  }

  onDateChangeHandler(ev: any) {
    if (!ev || !ev.startDate || !ev.endDate) {
      return;
    }
    this.selected = clampMtsRange(ev.startDate, ev.endDate);
    this.rangeNote = this.selected.startDate.isSame(moment(ev.startDate), "day")
      ? ""
      : `Ranges are limited to ${MTS_MAX_RANGE_DAYS} days - showing the last ${MTS_MAX_RANGE_DAYS}.`;
  }

  // What the request carries: a DAY when the page was deep-linked from the
  // summary, a range when it was opened from the menu.
  //
  // The day is passed through as a day rather than converted to a range here.
  // The server anchors a YYYYMMDD in the reporting timezone; building the
  // range in the browser anchors it in the BROWSER's, so a browser elsewhere
  // would ask for a different span of hours than the summary shows under the
  // same heading.
  private rangePayload(): any {
    if (!this.standalone) {
      return { day: this.day };
    }
    return {
      startTime: Math.floor(new Date(this.selected.startDate).getTime() / 1000),
      endTime: Math.floor(new Date(this.selected.endDate).getTime() / 1000),
    };
  }

  private rangeLabel(): string {
    const f = "DD MMM YYYY";
    const a = moment(this.selected.startDate).format(f);
    const b = moment(this.selected.endDate).format(f);
    return a === b ? a : `${a} – ${b}`;
  }

  setGroupProvider(g: string) {
    this.groupProvider = g === "Exchange" ? "Exchange" : "SportsBook";
    if (this.loaded) {
      this.load();
    }
  }

  back() {
    this.router.navigate(["/mts/reporting-summary"]);
  }

  // A sport opens in the report generator, filtered to it and to the period
  // being shown - and to the SECTION it was clicked in, so clicking Cricket
  // under "Sports / Live" lands on live cricket rather than on all of it.
  //
  // Everything goes in the URL rather than in a service: the drill-down is
  // then shareable, survives a reload, and the generator has no idea it was
  // reached from here.
  //
  // The period travels as whatever this page is holding - a day when it was
  // deep-linked, a range when it was opened from the menu. Sending `day: 0`
  // for a range would land the generator on no date at all.
  openSport(row: Row, section: { livePre: string }) {
    if (!row.key) {
      return;
    }
    const period: any = this.standalone
      ? {
          startTime: Math.floor(new Date(this.selected.startDate).getTime() / 1000),
          endTime: Math.floor(new Date(this.selected.endDate).getTime() / 1000),
        }
      : { day: this.day };
    this.router.navigate(["/mts/report-generator"], {
      queryParams: {
        ...period,
        sportId: row.key,
        // The resolved name, so the chip reads "Cricket" before the option
        // list has loaded rather than flashing the id.
        sportName: row.label,
        groupProvider: this.groupProvider,
        livePre: section.livePre || null,
        operators: this.operatorIds.length ? this.operatorIds.join(",") : null,
      },
    });
  }

  load() {
    if (!this.standalone && !this.day) {
      this.errorMessage = "No date to report on.";
      return;
    }
    if (this.standalone && (!this.selected || !this.selected.startDate)) {
      this.errorMessage = "Pick a date range first.";
      return;
    }
    this.loaded = true;
    this.loading = true;
    this.errorMessage = "";
    // The ranking belongs to the filters it was fetched under, so a new
    // report invalidates it. It is NOT refetched here - that happens when a
    // panel is opened, if one ever is.
    this.events.fetched = false;
    this.events.top = [];
    this.events.bottom = [];
    this.events.error = "";
    // With the rows it described. Left behind, the previous query's cut-off
    // sits beside "Loading…" as though it applied to the new one.
    this.events.asOf = 0;
    this.events.totals = null;
    if (this.events.topOpen || this.events.bottomOpen) {
      this.loadEvents();
    }

    // ONE request for all three tables.
    //
    // They were three, one per scope, and each was the same scan of the range
    // with a different livePre filter - there is no index on it, so every one
    // read every row and discarded most of what it read. The server groups on
    // sport AND live/pre in a single pass now and returns the three sections.
    const payload: any = {
      ...this.rangePayload(),
      groupProvider: this.groupProvider,
    };
    if (this.operatorIds.length) {
      payload.operatorIds = this.operatorIds;
    }

    this.appService
      .getMtsProfitability(payload)
      .pipe(timeout(REQUEST_TIMEOUT_MS))
      .subscribe(
        (res) => {
          this.loading = false;
          if (!res || res.status !== "RS_OK") {
            const msg = (res && res.errorDescription) || "This report could not be loaded.";
            for (const section of this.sections) {
              section.rows = [];
              section.totals = null;
              section.error = msg;
            }
            return;
          }
          // Matched on livePre rather than on position, so the server is free
          // to reorder or omit a section without the page silently showing
          // live figures under a pre-match heading.
          const bySection: any = {};
          for (const sec of res.sections || []) {
            bySection[sec.livePre || ""] = sec;
          }
          for (const section of this.sections) {
            const got = bySection[section.livePre] || null;
            section.rows = (got && got.rows) || [];
            // The totals come back with the rows, from the same rows, so a
            // section's summary cannot disagree with its own table.
            section.totals = (got && got.totals) || null;
            section.error = got ? "" : "This scope was not returned.";
          }
          // The Singles band, as its own request. Fetched rather than derived
          // by taking multiples off the total, which would put the counting
          // rules in a second place and let the two drift.
          this.loadSinglesBands(payload);
        },
        () => {
          this.loading = false;
          for (const section of this.sections) {
            section.rows = [];
            section.totals = null;
            section.error = "This report could not be loaded.";
          }
        }
      );
  }

  // loadSinglesBands fills the second band of every section.
  //
  // A failure leaves the bands empty rather than taking the page down: the
  // first band IS the report, and the second sits beside it for comparison.
  private loadSinglesBands(payload: any): void {
    for (const section of this.sections) {
      section.singlesByKey = {};
      section.singlesTotals = null;
    }
    this.appService
      .getMtsProfitability({ ...payload, betKind: "S" })
      .pipe(timeout(REQUEST_TIMEOUT_MS))
      .subscribe(
        (res) => {
          if (!res || res.status !== "RS_OK") {
            return;
          }
          const bySection: any = {};
          for (const sec of res.sections || []) {
            bySection[sec.livePre || ""] = sec;
          }
          for (const section of this.sections) {
            const got = bySection[section.livePre] || null;
            const byKey: { [key: string]: any } = {};
            for (const r of (got && got.rows) || []) {
              byKey[String(r.key || "")] = r;
            }
            section.singlesByKey = byKey;
            section.singlesTotals = (got && got.totals) || null;
          }
        },
        () => {
          /* the bands stay empty; the report itself is already on screen */
        }
      );
  }


  // A band cell can have no row behind it - a sport bet on only in combos has
  // no Singles figures, and the Singles totals are null until the second
  // request lands. The template guards the VALUE with *ngIf, but [ngClass] is
  // on the <td>, which is always created, so signClass() reaches here with
  // null on the first change-detection pass of every load.
  value(row: Row | null, col: Column): number {
    if (!row) {
      return 0;
    }
    const v = row[col.key];
    return typeof v === "number" ? v : 0;
  }

  format(row: Row, col: Column): string {
    const v = this.value(row, col);
    if (col.kind === "int") {
      return v.toLocaleString("en-IN");
    }
    if (col.kind === "pct") {
      return v.toFixed(2) + "%";
    }
    return v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  signClass(row: Row | null, col: Column): string {
    if (!col.signed) {
      return "";
    }
    const v = this.value(row, col);
    return v > 0 ? "pos" : v < 0 ? "neg" : "";
  }

  // The period's events, ranked by the book's P&L - highest at the top of one
  // panel, lowest at the top of the other.
  //
  // The RANKING IS THE SERVER'S. One request comes back with both ends already
  // chosen, because the alternative is being sent every event in the range to
  // show forty rows - on a busy week that is several hundred kilobytes encoded,
  // shipped and parsed to throw almost all of it away.
  //
  // Its failure is its own. An event panel that cannot load must not blank the
  // sport tables, which are the page's actual subject.
  loadEvents() {
    // Claimed BEFORE the request goes out, not in the callback. Marking it
    // fetched on success meant opening the second panel while the first was
    // still in flight saw "not fetched" and sent a duplicate.
    this.events.fetched = true;
    this.events.loading = true;
    this.events.error = "";
    const seq = ++this.events.seq;
    const payload: any = {
      ...this.rangePayload(),
      groupBy: "event",
      groupProvider: this.groupProvider,
      rankBy: "pnl",
      limit: this.eventPanelSize,
    };
    if (this.operatorIds.length) {
      payload.operatorIds = this.operatorIds;
    }

    this.appService
      .getMtsReportingSummary(payload)
      .pipe(timeout(REQUEST_TIMEOUT_MS))
      .subscribe(
        (res) => {
          if (seq !== this.events.seq) {
            return;
          }
          this.events.loading = false;
          if (!res || res.status !== "RS_OK") {
            // A failed fetch is not a fetch: let the next open try again
            // rather than showing the same error for ever.
            this.events.fetched = false;
            this.events.top = [];
            this.events.bottom = [];
            this.events.error =
              (res && res.errorDescription) || "The event ranking could not be loaded.";
            return;
          }
          // Both ends come back ranked and capped - rows is the best, and
          // bottomRows the worst, which the server leaves out entirely on a
          // period the book lost nothing in.
          this.events.top = res.rows || [];
          this.events.bottom = res.bottomRows || [];
          this.events.asOf = res.asOf || 0;
          this.events.totals = res.totals || null;
        },
        () => {
          if (seq !== this.events.seq) {
            return;
          }
          this.events.loading = false;
          this.events.fetched = false;
          this.events.top = [];
          this.events.bottom = [];
          this.events.error = "The event ranking could not be loaded.";
        }
      );
  }

  // Said plainly, because a ranking of what just went wrong that quietly
  // stops an hour ago reads as current.
  get eventsAsOfNote(): string {
    if (!this.events.asOf) {
      return "";
    }
    return `Ranked from collected data up to ${moment(this.events.asOf).format(
      "DD MMM, HH:mm"
    )}. Bets and settlements recorded since then are not in this ranking yet.`;
  }

  private formatDay(day: number): string {
    if (!day) {
      return "";
    }
    const s = String(day);
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  }

  get hasAnyRows(): boolean {
    return this.sections.some((s) => s.rows.length > 0);
  }

  downloadCsv() {
    const head = ["Section", "Sport", ...this.columns.map((c) => c.label)];
    const lines = [head.join(",")];
    for (const section of this.sections) {
      for (const r of section.rows) {
        lines.push(
          [section.title, r.label, ...this.columns.map((c) => this.value(r, c))].join(",")
        );
      }
      if (section.totals) {
        lines.push(
          [section.title, "Total", ...this.columns.map((c) => this.value(section.totals, c))].join(",")
        );
      }
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `mts-${this.dateLabel}-by-sport.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }
}
